/** Script review calls: types and API (docs/ARCHITECTURE-SCRIPT-CALLS.md). */
import { api } from './api';

export interface CallInvitee { email: string; name: string; kind: 'doctor' | 'clinic' | 'staff'; talent_id?: string | null; user_id?: string | null }
export interface CallCandidate { creative_id: string; code: string; title: string; script_status: string; eligible: boolean; reason?: string | null; on_call: boolean }
export interface CallCandidateGroup { talent_id?: string | null; talent_name: string; scripts: CallCandidate[] }
export interface CallCandidates {
  client_id: string; client_name: string; groups: CallCandidateGroup[];
  suggested_invitees: CallInvitee[]; organizers: Array<{ id: string; name: string }>; dry_run: boolean;
}
export interface MeetingDelivery { id: string; recipient: string; channel: 'email' | 'telegram'; kind: string; status: string; error?: string | null }
export interface MeetingCreative {
  creative_id: string; code: string; title: string; talent_name?: string | null; script_status: string;
  final_outcome?: 'approved' | 'changes_requested' | 'not_discussed' | null; decided_outside: boolean; filed_at?: string | null;
  changed_since_booking: boolean; decided_by_name?: string | null; staff_note?: string | null; clinic_note?: string | null;
}
export type MeetingStatus = 'link_sent' | 'booking' | 'booked' | 'awaiting_audio' | 'transcribing' | 'review' | 'filed'
  | 'expired' | 'cancelled' | 'no_show' | 'failed';
export interface Meeting {
  id: string; kind: string; client_id?: string | null; client_name?: string | null; title: string;
  organizer_id: string; organizer_name?: string | null; status: MeetingStatus;
  starts_at?: string | null; ends_at?: string | null; meet_uri?: string | null;
  invitees: CallInvitee[]; link?: string | null; token_expires_at?: string | null; link_opened_at?: string | null;
  suggestion?: string | null; deliveries: MeetingDelivery[]; creatives: MeetingCreative[];
  dry_run: boolean; last_error?: string | null; can_manage: boolean; created_at: string;
  // general meetings (migration 057)
  purpose?: 'script_call' | 'meeting'; length_minutes?: number | null; with_meet?: boolean; location?: string | null;
  notes?: string | null; attendees?: Person[]; clients?: Person[]; items?: MeetingItem[]; follow_ups?: FollowUp[];
  follow_up_of?: string | null; shoot_id?: string | null;
  // the KAF Recorder (migration 059)
  recorder_state?: string | null; recorder_note?: string | null; recorder_at?: string | null;
  has_transcript?: boolean; transcript_pending?: boolean;
  // KAF Minutes (migration 060)
  summary?: string | null; minutes_state?: 'pending' | 'done' | 'failed' | 'off' | null;
  // the minutes in full (migration 064); and whether the organiser is in the meeting
  minutes_doc?: MinutesDoc | null; organizer_attends?: boolean; audio_kept?: boolean;
}
export interface MinutesDoc {
  summary?: string | null;
  topics: { title: string; at?: string | null; points: string[]; unsure?: boolean }[];
  decisions: { text: string; at?: string | null; unsure?: boolean }[];
  open_questions: { text: string; at?: string | null }[];
  actions: { title: string; details?: string | null; side: 'kaf' | 'clinic'; owner?: string | null; owner_hint?: string | null;
    due_date?: string | null; when_said?: string | null; at?: string | null; quote?: string | null; unsure?: boolean }[];
  quality?: { words: number; minutes: number; coverage_pct: number; lost_minutes: number; loop_lines: number; level: 'good' | 'partial' | 'poor'; note?: string | null };
}
export interface Person { id: string; name: string }
export type Outcome = 'approved' | 'changes' | 'discussed' | 'action';
export interface MeetingItem {
  id: string; kind: 'creative' | 'strategy' | 'clinic' | 'topic'; label: string; creative_id?: string | null;
  strategy_id?: string | null; client_id?: string | null; script_pending: boolean; outcome?: Outcome | null;
  clinic_note?: string | null; staff_note?: string | null; filed_at?: string | null;
  suggested_outcome?: Outcome | null; suggested_note?: string | null;
}
export interface FollowUp {
  id: string; item_id?: string | null; creative_id?: string | null; creative_code?: string | null; assignee_id?: string | null;
  assignee_name?: string | null; title: string; due_date?: string | null; notes?: string | null; status: 'draft' | 'sent' | 'dropped' | 'done';
  task_id?: string | null; source?: 'manual' | 'kaf_minutes'; when_said?: string | null; done_at?: string | null;
}
export interface MeetingAction {
  id: string; title: string; notes?: string | null; when_said?: string | null; due_date?: string | null; status: 'sent' | 'done';
  done_at?: string | null; meeting_id: string; meeting_title: string; organizer_name?: string | null;
}
export interface SetupData {
  team: Array<{ id: string; name: string; designations: string[] }>;
  clinics: Person[];
  invitees: Array<{ email: string; name: string; kind: 'doctor' | 'clinic'; client_id: string; talent_id?: string | null; user_id?: string | null }>;
  agenda: Array<{ kind: 'creative' | 'strategy'; id: string; label: string; client_id: string; script_pending: boolean }>;
}
export interface PlanItem { kind: 'creative' | 'strategy' | 'clinic' | 'topic'; id?: string; label?: string }
export interface PlanBody {
  kind: 'internal' | 'client'; title?: string; attendee_ids: string[]; client_ids: string[];
  invitees: Array<{ email: string; name: string; kind: 'doctor' | 'clinic'; client_id?: string; talent_id?: string | null; user_id?: string | null }>;
  items: PlanItem[]; minutes: number; with_meet: boolean; location?: string; notes?: string; start?: string;
  follow_up_of?: string; shoot_id?: string; organizer_attends?: boolean;
}
export interface Conflict { user_id: string; name: string; reason: 'off' | 'meeting' | 'shoot' | 'hours'; label: string }

export interface TranscriptLine { start: number; end: number; text: string }
export interface ReviewScript {
  creative_id: string; code: string; title: string; talent_name?: string | null; script?: string | null; script_status: string;
  quotes: number[]; changed_since_booking: boolean; decided_outside: boolean; filed_at?: string | null; final_outcome?: string | null;
}
export interface MeetingReview { meeting_id: string; title: string; starts_at?: string | null; minutes?: number | null;
  transcript: TranscriptLine[]; general: number[]; scripts: ReviewScript[] }
export interface FileItem {
  creative_id: string; outcome: 'approved' | 'changes_requested' | 'not_discussed';
  decided_by_name?: string; staff_note?: string; clinic_note?: string; override?: boolean;
}
export interface BookingHours {
  tz: string; days: number[]; start: string; end: string; minutes: number; notice_hours: number; horizon_days: number; buffer?: number;
  /** 2026-09-29: hours per weekday ("1".."7"), several ranges a day, and per-date rules */
  week?: Record<string, [string, string][]>; dates?: Record<string, { off?: boolean; ranges?: [string, string][] }>;
}

export const fetchCallCandidates = async (creativeId: string) =>
  (await api.get<CallCandidates>('/meetings/candidates', { params: { creative_id: creativeId } })).data;
export const createMeeting = async (body: { creative_ids: string[]; invitees: CallInvitee[]; organizer_id?: string }) =>
  (await api.post<Meeting>('/meetings', body)).data;
export const fetchMeetings = async (params: { creative_id?: string; open?: boolean }) =>
  (await api.get<Meeting[]>('/meetings', { params })).data;
export const fetchMeeting = async (id: string) => (await api.get<Meeting>(`/meetings/${id}`)).data;
export const cancelMeeting = async (id: string) => (await api.post<Meeting>(`/meetings/${id}/cancel`)).data;
export const handoverMeeting = async (id: string, organizer_id: string) => (await api.post<Meeting>(`/meetings/${id}/handover`, { organizer_id })).data;
export const newMeetingLink = async (id: string) => (await api.post<Meeting>(`/meetings/${id}/new-link`)).data;
export const resendDelivery = async (id: string, rowId: string) => (await api.post<Meeting>(`/meetings/${id}/deliveries/${rowId}/resend`)).data;
export const startCallAudio = async (id: string, file: File) =>
  (await api.post<{ upload_url: string; object: string }>(`/meetings/${id}/audio/start`,
    { filename: file.name, content_type: file.type || 'audio/mpeg', size: file.size })).data;
export const finishCallAudio = async (id: string, object: string) => (await api.post<Meeting>(`/meetings/${id}/audio/done`, { object })).data;
export const fetchMeetingReview = async (id: string) => (await api.get<MeetingReview>(`/meetings/${id}/review`)).data;
export const fileMeeting = async (id: string, items: FileItem[], mentions: string[] = []) =>
  (await api.post<Meeting>(`/meetings/${id}/file`, { items, mentions })).data;
export const fetchMeetingSetup = async (clientIds: string[], q = '') =>
  (await api.get<SetupData>('/meetings/setup', { params: { client_ids: clientIds.join(','), q } })).data;
export const planMeeting = async (body: PlanBody) => (await api.post<Meeting>('/meetings/plan', body)).data;
export const redraftMinutes = async (id: string) => (await api.post<Meeting>(`/meetings/${id}/minutes/redraft`)).data;
export const fetchRecording = async (id: string) => (await api.get<{ url: string | null; kept: boolean }>(`/meetings/${id}/recording`)).data;
export const retranscribe = async (id: string) => (await api.post<Meeting>(`/meetings/${id}/retranscribe`)).data;
export const setMeetingItem = async (id: string, itemId: string, body: { outcome?: Outcome | null; clinic_note?: string; staff_note?: string }) =>
  (await api.put<Meeting>(`/meetings/${id}/items/${itemId}`, body)).data;
export const fileMeetingItems = async (id: string, sendSummary: boolean) =>
  (await api.post<Meeting>(`/meetings/${id}/file-items`, { send_summary: sendSummary })).data;
export const addFollowUp = async (id: string, body: Partial<FollowUp>) => (await api.post<Meeting>(`/meetings/${id}/follow-ups`, body)).data;
export const editFollowUp = async (fid: string, body: Partial<FollowUp>) => (await api.patch<Meeting>(`/meeting-follow-ups/${fid}`, body)).data;
export const dropFollowUp = async (fid: string) => (await api.delete<Meeting>(`/meeting-follow-ups/${fid}`)).data;
export const sendFollowUps = async (id: string, ids: string[]) => (await api.post<Meeting>(`/meetings/${id}/follow-ups/send`, { ids })).data;
export const fetchMyActions = async () => (await api.get<MeetingAction[]>('/me/meeting-actions')).data;
export const markActionDone = async (fid: string) => (await api.post<MeetingAction>(`/meeting-follow-ups/${fid}/done`)).data;
export const fetchBookingHours = async () => (await api.get<BookingHours>('/me/booking-hours')).data;
export const saveBookingHours = async (h: BookingHours) => (await api.put<BookingHours>('/me/booking-hours', h)).data;

/** A PUT of the whole file to the resumable session storage gave us, with progress. */
export function putToSession(url: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'audio/mpeg');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload interrupted'));
    xhr.send(file);
  });
}

export const MEETING_STATUS: Record<MeetingStatus, { label: string; cls: string }> = {
  link_sent:      { label: 'Link sent',            cls: 'bg-sky-500/15 text-sky-300' },
  booking:        { label: 'Booking',              cls: 'bg-sky-500/15 text-sky-300' },
  booked:         { label: 'Booked',               cls: 'bg-emerald-500/15 text-emerald-300' },
  awaiting_audio: { label: 'Waiting for audio',    cls: 'bg-amber-500/15 text-amber-300' },
  transcribing:   { label: 'Transcribing',         cls: 'bg-violet-500/15 text-violet-300' },
  review:         { label: 'Ready to confirm',     cls: 'bg-amber-500/15 text-amber-300' },
  filed:          { label: 'Filed',                cls: 'bg-slate-700/40 text-slate-300' },
  expired:        { label: 'Link expired',         cls: 'bg-red-500/15 text-red-300' },
  cancelled:      { label: 'Cancelled',            cls: 'bg-slate-700/40 text-slate-300' },
  no_show:        { label: 'No show',              cls: 'bg-red-500/15 text-red-300' },
  failed:         { label: 'Could not transcribe', cls: 'bg-red-500/15 text-red-300' },
};

export function dubai(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-AE', { timeZone: 'Asia/Dubai', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** The ready-made WhatsApp message for the booking link, Arabic first. */
export function whatsappText(clinicPerson: string, count: number, link: string): string {
  return `مرحبًا ${clinicPerson}، يُرجى اختيار موعد لمراجعة ${count === 1 ? 'النص' : 'النصوص'} معنا عبر هذا الرابط:\n${link}\n\n`
    + `Hello ${clinicPerson}, please choose a time to go through ${count === 1 ? 'the script' : 'the scripts'} with us:\n${link}`;
}
